import {Injectable} from "@angular/core";
import {DefaultEventsMap} from "@socket.io/component-emitter";
import {Socket} from "socket.io-client";
import {Vector2, Vector3} from "three";
import * as uuid from "uuid";
import {Action, Store} from "@ngrx/store";
import {networkActions} from "../../state/network/network.actions";
import {NetworkConnectedState} from "../../state/network/network-states.enum";
import {FunctionIsNotAllowed} from "node_modules/@ngrx/store/src/models";
import {selectCameraTarget} from "../../state/camera/camera.selector";
import {EntitySeederService} from "./entity-seeder.service";
import * as AuthSelectors from '../../state/auth/auth.selector';
import {User} from "../../auth/primitives/user";
import {WebsocketService} from "../websocket.service";
import {entityActions} from "../../state/entities/entity.actions";
import {IHasSocket} from "../i-has-socket";
import {selectWorldReady} from "../../state/world/world.selector";
import {sampleTime} from "rxjs";
import * as networkSelectors from "../../state/network/network.selector";
import {CollisionType, Components, EntityType} from "../../state/entities/entity.model";
import {LookingAtProperties} from "../../state/entities/component-properties-dto/looking-at.properties.dto";
import {DoWhenReadyService, Readyables} from "../do-when-ready.service";
import {ServerPayload} from "../dtos/server-payload.dto";

@Injectable({
  providedIn: "root",
})
export class EventsWebsocketService implements IHasSocket {
  namespace = ""
  public socket: Socket<DefaultEventsMap, DefaultEventsMap>;
  myGuid = uuid.v4();
  updateFuncs: Function[] = [];

  coordTopics = [];

  user: User = null

  gridSize = 1000;
  grid: Vector3

  readyToSeed = false
  seededCoords = []
  debug = false

  constructor(
      private store: Store,
      private entitySeederService: EntitySeederService,
      private websocketService: WebsocketService,
      private doWhenReady: DoWhenReadyService
  ) {
    this.websocketService.eventsSocket.subscribe(async (socket) => {
      if (!socket) return
      this.socket = socket
      this.socket.connect()
      this.setupEventHandlers();
      this.setupActionHandlers();
    })
    this.doWhenReady.everyTime(this, [Readyables.network], async (user) => {
      await this.makeContact()
      this.store.select(selectWorldReady).subscribe(async (isReady) => {
        if (!isReady) return
        await this.seedEntities()
        this.readyToSeed = true
      })
      // this.store.select(AuthSelectors.selectUser).subscribe((user) => {
      //   const hadUser = !!user
      //   if (!user) return
      // })
      this.user = user
      await this.setupAvatar(user.id, JSON.parse(user.last_coord))
      await this.setupCameraTargetCoordSeeder()
    })
  }

  log(message: string) {
    if (this.debug) {
      console.log(message)
    }
  }

  async setupCameraTargetCoordSeeder() {
    this.store.select(selectCameraTarget).pipe(
        sampleTime(1000)
    ).subscribe(async (target) => {
      if (!target) return
      const newGrid = new Vector3(
          Math.ceil(target.x / this.gridSize),
          Math.ceil(target.y / this.gridSize),
          0,
      );
      if (this.grid && (newGrid.equals(this.grid))) return
      this.log("New grid:" + newGrid)
      await this.setupAvatar(this.user.id, target)
      this.grid = newGrid
      await this.setupCoords(target);
      if (this.readyToSeed) {
        await this.seedEntities()
      }
    });
  }

  async setupAvatar(userId, startTarget: Vector3) {
    this.dispatch(
        entityActions.netCreated({
          entity: {
            id: userId,
            type: EntityType.UserAvatar,
            ownerId: userId,
            components: [Components.lookingAt],
            coord: startTarget,
            componentProperties: {
              LookingAt: {
                target: new Vector3(0,0,0)
              } as LookingAtProperties
            },
            collisionType: CollisionType.Obstacle,
            creationDate: new Date(),
          },
        }),
    );
  }

  async setupCoords(target: Vector3) {
    const coord = target;
    const grid = new Vector3(
        Math.ceil(coord.x / this.gridSize),
        Math.ceil(coord.y / this.gridSize),
        0,
    );
    const surrounds = [
      new Vector3(grid.x, grid.y, grid.z),
      new Vector3(grid.x - 1, grid.y, grid.z),
      new Vector3(grid.x - 1, grid.y + 1, grid.z),
      new Vector3(grid.x, grid.y + 1, grid.z),
      new Vector3(grid.x + 1, grid.y + 1, grid.z),
      new Vector3(grid.x + 1, grid.y, grid.z),
      new Vector3(grid.x + 1, grid.y - 1, grid.z),
      new Vector3(grid.x, grid.y - 1, grid.z),
      new Vector3(grid.x - 1, grid.y - 1, grid.z),
    ];
    const topics = surrounds.map(
        (grid) =>
            `coord_${grid.x.toFixed(0)}-${grid.y.toFixed(0)}-${grid.z.toFixed(0)}`,
    );
    // console.log(topics);
    this.coordTopics = topics;
  }

  async makeContact() {
    await this.socket.emitWithAck("init_connect", {
      owner: this.user?.id || 'guest',
      topics: ["coord_0-0-0"],
    });
    console.log("Contact with server")
  }

  async seedEntities() {
    let responseCount = 0
    for (const topic of this.coordTopics) {
      if (this.seededCoords.includes(topic)) {
        this.log("[WebsocketService] Coord already seeded: " + topic + " - skipping.")
        continue
      }
      this.log("[WebsocketService] Seeding Coord " + topic)
      const response: string[] = await this.socket.emitWithAck("get_entities_for_coord", {
        coord: topic,
      });
      responseCount += response.length
      if (response) {
        await this.entitySeederService.seedCoord(topic, response)
        this.seededCoords.push(topic)
      }
      this.log(`[WebsocketService] Seeding Coord ${topic} completed with ${responseCount} entities`)
    }
  }

  async updateTopics() {
    const allTopics = [...this.coordTopics];
    this.socket.emit("update_topics", {
      owner: this.myGuid,
      topics: allTopics,
    });
  }

  async disconnect() {
    this.socket.disconnect();
  }

  setupEventHandlers() {
    this.socket.on("connect", () => {
      this.makeContact()
    });
    this.socket.on("init_data", () => {
      this.log("INIT DATA");
    });
  }

  setupActionHandlers() {
    this.socket.on("Action", (jsonData) => {
      let data = jsonData;
      return this._gotServerAction(data);
    });
  }

  dispatch<V extends Action = Action>(
    action: V &
      FunctionIsNotAllowed<
        V,
        "Functions are not allowed to be dispatched. Did you forget to call the action creator function?"
      >,
  ) {
    let { type, ...body } = action;
    const route = type.slice(1, type.lastIndexOf("]"));
    const actionName = type.slice(type.lastIndexOf("]") + 2);
    this.dispatchRoute({
      route,
      action: actionName,
      body,
    });
    // console.log(actionName);
  }

  _gotServerAction(payload: ServerPayload) {
    // const tmpActions = actions[payload.route];
    // const action = tmpActions[payload.action];
    // const data =
    const actionName = `[${payload.route}] ${payload.action}`;
    const body = JSON.parse(payload.data);
    let action = {
      type: actionName,
      ...body,
    } as Action;
    // console.log("Got action:", JSON.stringify(action));
    this.store.dispatch(action);
  }

  private dispatchRoute(config: {
    route: string;
    action: string;
    body: Object;
    location?: Vector2;
  }) {
    let location = config.location;
    if (!config.location) {
      location = new Vector2(0, 0);
    }
    const topic = `coord_${parseInt(location.x.toFixed(0))}-${parseInt(location.y.toFixed(0))}-0`;
    this.socket.emit("action", {
      route: config.route,
      action: config.action,
      data: config.body,
      topic: topic,
    });
  }

  emit(topic: string, data: any) {
    return new Promise((resolve, reject) => {
      try {
        this.socket.timeout(1000).emitWithAck(topic, data, (response: any) => {
          resolve(response);
        });
      } catch (err) {
        reject(err);
      }
    });
  }
}
